import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LANDING } from "@/constants/testIds";
import { Check, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES } from "@/components/landing/Services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYEE_BUCKETS = [
  "1-10",
  "11-50",
  "51-100",
  "101-250",
  "251-500",
  "501-1000",
  "1000+",
];

const INDUSTRIES = [
  "Construction & trades",
  "Manufacturing",
  "Professional services",
  "Technology & SaaS",
  "Healthcare",
  "Retail & hospitality",
  "Transportation & logistics",
  "Non-profit",
  "Other",
];

const STEPS = ["Business", "Coverage", "Contact"];

const initial = {
  company_name: "",
  industry: "",
  employee_count: "",
  products: [],
  coverage_start: "",
  current_carrier: "",
  contact_name: "",
  email: "",
  phone: "",
  notes: "",
};

export default function QuoteFormSection() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleProduct = (slug) => {
    setData((d) => {
      const has = d.products.includes(slug);
      return {
        ...d,
        products: has
          ? d.products.filter((p) => p !== slug)
          : [...d.products, slug],
      };
    });
    setErrors((e) => ({ ...e, products: undefined }));
  };

  const validate = (s) => {
    const e = {};
    if (s === 0) {
      if (!data.company_name.trim()) e.company_name = "Required";
      if (!data.industry) e.industry = "Required";
      if (!data.employee_count) e.employee_count = "Required";
    }
    if (s === 1) {
      if (data.products.length === 0)
        e.products = "Select at least one coverage line";
    }
    if (s === 2) {
      if (!data.contact_name.trim()) e.contact_name = "Required";
      if (!data.email.trim() || !/.+@.+\..+/.test(data.email))
        e.email = "Valid email required";
      if (!data.phone.trim()) e.phone = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validate(step)) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validate(2)) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, data);
      setSuccess(true);
      toast.success("Quote request received", {
        description: "A broker will reach out within one business day.",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong submitting your quote.";
      toast.error("Submission failed", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setData(initial);
    setErrors({});
    setStep(0);
    setSuccess(false);
  };

  return (
    <section
      id="quote"
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-primary text-primary-foreground"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left intro */}
        <div className="lg:col-span-5">
          <p className="label-eyebrow text-accent mb-4">05 — Quote</p>
          <h2 className="font-display text-4xl md:text-5xl h-tight font-medium">
            A real quote.{" "}
            <span className="italic font-light text-accent">In about a minute.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-primary-foreground/75 max-w-md">
            Tell us a little about your business and the coverage you&apos;re
            after. A licensed broker reviews every submission personally — no
            call center, no robocalls.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            {[
              "Response within one business day",
              "No obligation, no soft credit pulls",
              "We work for you, not the carrier",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <Check
                  size={16}
                  className="text-accent shrink-0"
                  strokeWidth={2.4}
                />
                <span className="text-primary-foreground/85">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Form card */}
        <div className="lg:col-span-7">
          <div
            data-testid={LANDING.quoteForm}
            className="bg-background text-foreground rounded-md border border-border p-6 md:p-10 shadow-sm"
          >
            {success ? (
              <div
                data-testid={LANDING.quoteFormSuccess}
                className="text-center py-10"
              >
                <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto">
                  <Sparkles size={22} />
                </div>
                <h3 className="font-display text-3xl font-medium mt-6">
                  Your request is in.
                </h3>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                  A broker will reach out within one business day to{" "}
                  <span className="font-medium text-foreground">
                    {data.email}
                  </span>{" "}
                  with next steps and any clarifying questions.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-md border border-border text-sm font-semibold hover:bg-secondary transition-colors"
                >
                  Submit another quote
                </button>
              </div>
            ) : (
              <>
                {/* Stepper */}
                <div
                  data-testid={LANDING.quoteFormStepIndicator}
                  className="flex items-center justify-between gap-4 mb-8 pb-6 border-b border-border"
                >
                  {STEPS.map((label, i) => {
                    const active = i === step;
                    const done = i < step;
                    return (
                      <div
                        key={label}
                        className="flex-1 flex items-center gap-3"
                      >
                        <span
                          className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            done
                              ? "bg-primary text-primary-foreground"
                              : active
                              ? "bg-accent text-accent-foreground"
                              : "bg-secondary text-muted-foreground"
                          }`}
                        >
                          {done ? <Check size={14} /> : i + 1}
                        </span>
                        <span
                          className={`text-xs uppercase tracking-[0.2em] font-semibold hidden sm:inline ${
                            active || done
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }`}
                        >
                          {label}
                        </span>
                        {i < STEPS.length - 1 && (
                          <span className="hidden sm:block flex-1 h-px bg-border" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Step bodies */}
                {step === 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <Label htmlFor="company">Business name</Label>
                      <Input
                        id="company"
                        data-testid={LANDING.quoteFormCompany}
                        value={data.company_name}
                        onChange={(e) => set("company_name", e.target.value)}
                        placeholder="Acme Holdings, Inc."
                        className="mt-2"
                      />
                      {errors.company_name && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.company_name}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Industry</Label>
                      <Select
                        value={data.industry}
                        onValueChange={(v) => set("industry", v)}
                      >
                        <SelectTrigger
                          data-testid={LANDING.quoteFormIndustry}
                          className="mt-2"
                        >
                          <SelectValue placeholder="Choose an industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map((i) => (
                            <SelectItem key={i} value={i}>
                              {i}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.industry && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.industry}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Employees</Label>
                      <Select
                        value={data.employee_count}
                        onValueChange={(v) => set("employee_count", v)}
                      >
                        <SelectTrigger
                          data-testid={LANDING.quoteFormEmployees}
                          className="mt-2"
                        >
                          <SelectValue placeholder="Headcount" />
                        </SelectTrigger>
                        <SelectContent>
                          {EMPLOYEE_BUCKETS.map((b) => (
                            <SelectItem key={b} value={b}>
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.employee_count && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.employee_count}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-sm">
                        Which coverage lines are you interested in?
                      </Label>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {SERVICES.map((s) => {
                          const checked = data.products.includes(s.slug);
                          return (
                            <button
                              key={s.slug}
                              type="button"
                              data-testid={LANDING.quoteFormProduct(s.slug)}
                              onClick={() => toggleProduct(s.slug)}
                              className={`text-left rounded-md border p-4 transition-all duration-200 ${
                                checked
                                  ? "border-primary bg-primary/5"
                                  : "border-border hover:bg-secondary"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-semibold text-sm">
                                  {s.title}
                                </span>
                                <span
                                  className={`h-5 w-5 rounded-sm border flex items-center justify-center ${
                                    checked
                                      ? "bg-primary border-primary text-primary-foreground"
                                      : "bg-background border-border"
                                  }`}
                                >
                                  {checked && <Check size={12} />}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                {s.blurb}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                      {errors.products && (
                        <p className="text-xs text-destructive mt-2">
                          {errors.products}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="start">Coverage start date</Label>
                        <Input
                          id="start"
                          type="date"
                          data-testid={LANDING.quoteFormStartDate}
                          value={data.coverage_start}
                          onChange={(e) =>
                            set("coverage_start", e.target.value)
                          }
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="carrier">Current carrier (if any)</Label>
                        <Input
                          id="carrier"
                          data-testid={LANDING.quoteFormCarrier}
                          value={data.current_carrier}
                          onChange={(e) =>
                            set("current_carrier", e.target.value)
                          }
                          placeholder="e.g. Aetna, The Hartford"
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="cname">Your name</Label>
                      <Input
                        id="cname"
                        data-testid={LANDING.quoteFormContactName}
                        value={data.contact_name}
                        onChange={(e) => set("contact_name", e.target.value)}
                        className="mt-2"
                        placeholder="Jane Owner"
                      />
                      {errors.contact_name && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.contact_name}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email">Work email</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid={LANDING.quoteFormEmail}
                        value={data.email}
                        onChange={(e) => set("email", e.target.value)}
                        className="mt-2"
                        placeholder="jane@acme.com"
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid={LANDING.quoteFormPhone}
                        value={data.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        className="mt-2"
                        placeholder="(555) 123-4567"
                      />
                      {errors.phone && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="notes">
                        Anything else we should know? (optional)
                      </Label>
                      <Textarea
                        id="notes"
                        data-testid={LANDING.quoteFormNotes}
                        value={data.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        className="mt-2 min-h-[110px]"
                        placeholder="Specific claims history, contractual requirements, renewal date, etc."
                      />
                    </div>
                  </div>
                )}

                {/* Footer actions */}
                <div className="mt-8 pt-6 border-t border-border flex items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={back}
                    disabled={step === 0}
                    data-testid={LANDING.quoteFormBack}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                    Back
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button
                      type="button"
                      onClick={next}
                      data-testid={LANDING.quoteFormNext}
                      className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-md text-sm font-semibold hover:bg-[#1F1F1F] transition-colors"
                    >
                      Continue
                      <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={submit}
                      disabled={submitting}
                      data-testid={LANDING.quoteFormSubmit}
                      className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-md text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Submit quote request"}
                      <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
